import {useState, type FormEvent} from 'react';
import {Link, Navigate, useNavigate} from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Columns3,
  ShieldCheck,
  Users,
  X,
  type LucideIcon,
} from 'lucide-react';
import {api, trpc} from '@common-web/lib/trpc';
import {Combobox, containsFilter} from '@common-web/components/ui/combobox';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {EnrollEvent, useEnrollAnalytics} from '@client/pages/public/enroll/enrollAnalytics';
import {setEnrollAccess} from '@client/pages/public/enroll/enrollAccess';
import {accessCodeEntryMode, groupRequiresAccessCode} from '@client/pages/public/enroll/types';
import {useEnrollPreview} from '@client/pages/public/enroll/enrollPreview';

// Value props beside the code entry — identical across groups, so not in group config.
const FEATURES: {Icon: LucideIcon; title: string; body: string}[] = [
  {
    Icon: Columns3,
    title: 'Compare plans side-by-side',
    body: "See exactly what's covered at each tier, with checkmarks for every benefit and pricing pinned at the bottom.",
  },
  {
    Icon: ShieldCheck,
    title: 'Built-in compliance',
    body: 'ACA-compliant preventive care, decline-coverage confirmation, COBRA + Medicare Part D notices — all baked in.',
  },
  {
    Icon: Users,
    title: 'Add your dependents',
    body: 'Enroll spouse + children in one flow. Pricing updates live as you change your coverage tier.',
  },
];

export function EnrollAccessCode() {
  const group = useEnrollGroup();
  const {isPreview, isDemo} = useEnrollPreview();
  const {track} = useEnrollAnalytics();
  const navigate = useNavigate();
  const phoneDigits = group.supportPhone.replace(/[^\d]/g, '');
  const {headline, blurb, placeholder} = group.accessCodeScreen;
  const codeTerm = group.accessCodeTerm ?? 'access code';
  const gated = groupRequiresAccessCode(group);
  const dropdown = gated && accessCodeEntryMode(group) === 'dropdown';

  // Live sites read the public list (gated server-side on published dropdown mode); the
  // builder preview renders an unpublished draft, so it reads through the internal router.
  const publicLocations = trpc.enrollment.listLocations.useQuery(
    {groupSlug: group.slug},
    {enabled: dropdown && !isPreview, staleTime: 5 * 60 * 1000},
  );
  // Demo is a public site, so the internal preview-locations query isn't available — skip it;
  // the demo renders a disabled location field anyway (the step is skippable).
  const previewLocations = trpc.internal.enrollmentSite.locationOptions.useQuery(
    {groupSlug: group.slug},
    {enabled: dropdown && isPreview && !isDemo},
  );
  const locations = (isPreview ? previewLocations : publicLocations).data ?? [];
  // Show "City, ST" when the location has it; most labels already carry the city, so fall back to the label.
  const locationItems = locations.map((l) => {
    const place = [l.city, l.state].filter(Boolean).join(', ');
    return {value: l.accessCode, label: place || l.label};
  });

  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hintOpen, setHintOpen] = useState(true);

  // Public groups have no code screen — a direct visit goes straight into the flow.
  if (!gated) {
    return <Navigate to={`/${group.slug}/medical`} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    // Preview: skip the real code check and drop straight into the flow.
    if (isPreview) {
      setEnrollAccess(group.slug, {
        accessCode: 'PREVIEW',
        label: 'Preview',
        validatedAt: new Date().toISOString(),
      });
      navigate(`/${group.slug}/medical`);
      return;
    }
    const accessCode = code.trim();
    if (!accessCode || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const location = await api.enrollment.validateAccessCode.query({
        groupSlug: group.slug,
        accessCode,
      });
      setEnrollAccess(group.slug, {
        accessCode: location.accessCode,
        label: location.label,
        validatedAt: new Date().toISOString(),
      });
      track(EnrollEvent.AccessCodeSubmitted, {success: true});
      navigate(`/${group.slug}/medical`);
    } catch (err) {
      track(EnrollEvent.AccessCodeSubmitted, {success: false});
      setError(
        err instanceof Error && err.message
          ? err.message
          : "We couldn't verify that code. Please try again.",
      );
      setSubmitting(false);
    }
  }

  return (
    <div className='space-y-8'>
      <Link
        to={`/${group.slug}`}
        className='inline-flex items-center gap-1 text-sm text-neutral-500 transition-colors hover:text-[var(--enroll-ink)]'
      >
        <ArrowLeft className='h-4 w-4' />
        Back to home
      </Link>

      <div className='grid gap-10 lg:grid-cols-2 lg:items-start'>
        {/* Welcome + code entry */}
        <div>
          <h1
            className={`mb-3 text-4xl font-bold leading-tight tracking-tight text-[var(--enroll-ink)] lg:text-5xl ${POSTHOG_UNMASK_CLASS}`}
          >
            {headline.map((segment, index) => (
              <span
                key={index}
                className={segment.accent ? 'text-[var(--enroll-accent)]' : undefined}
              >
                {segment.text}
              </span>
            ))}
          </h1>
          <p className={`mb-8 max-w-xl text-lg text-neutral-500 ${POSTHOG_UNMASK_CLASS}`}>
            {blurb}
          </p>

          <div className='space-y-4 rounded-2xl border border-neutral-200 bg-white p-6'>
            <form onSubmit={handleSubmit} className='space-y-2'>
              <label
                htmlFor='access-code'
                className={`block text-sm font-semibold text-neutral-900 ${POSTHOG_UNMASK_CLASS}`}
              >
                {dropdown ? 'Your location' : `Your location ${codeTerm}`}
              </label>
              <div className='flex gap-3'>
                {dropdown && isDemo ? (
                  <div className='flex h-12 flex-1 items-center rounded-lg border border-neutral-300 bg-neutral-50 px-4 text-base text-neutral-400'>
                    Location picker (disabled in demo)
                  </div>
                ) : dropdown ? (
                  <Combobox
                    items={locationItems}
                    filter={containsFilter}
                    value={code}
                    setValue={setCode}
                    message='Select your location'
                    placeholder='Search by city or state'
                    emptyMessage={
                      publicLocations.isLoading || previewLocations.isLoading
                        ? 'Loading locations…'
                        : 'No matching location.'
                    }
                    className='h-12 flex-1 rounded-lg border-neutral-300 bg-white px-4 text-base shadow-none focus:ring-2 focus:ring-[var(--enroll-accent)]/30 data-[state=open]:ring-2 data-[state=open]:ring-[var(--enroll-accent)]/30'
                  />
                ) : (
                  <input
                    id='access-code'
                    type='text'
                    inputMode='numeric'
                    autoComplete='off'
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    placeholder={placeholder}
                    aria-invalid={error ? true : undefined}
                    className='h-12 flex-1 rounded-lg border border-neutral-300 px-4 text-base outline-none transition-colors focus:border-[var(--enroll-accent)] focus:ring-2 focus:ring-[var(--enroll-accent)]/30'
                  />
                )}
                {/* Anchored callout, not a Popover — wrapping the submit button in a
                    Popover broke native Enter-to-submit for everyone. */}
                <div className='relative'>
                  <button
                    type='submit'
                    disabled={submitting || (!isPreview && !code.trim())}
                    className={`inline-flex h-12 flex-none items-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-6 text-base font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60 ${POSTHOG_UNMASK_CLASS}`}
                  >
                    {submitting ? 'Checking…' : 'Continue'}
                    {!submitting && <ArrowRight className='h-[18px] w-[18px]' />}
                  </button>
                  {isPreview && hintOpen && (
                    <div className='absolute right-0 top-full z-10 mt-2 w-60 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 shadow-md'>
                      <button
                        type='button'
                        onClick={() => setHintOpen(false)}
                        className='absolute right-1.5 top-1.5 text-amber-500 hover:text-amber-800'
                        aria-label='Dismiss'
                      >
                        <X className='h-3.5 w-3.5' />
                      </button>
                      <span className='font-semibold'>{isDemo ? 'Demo' : 'Preview'}</span> — press
                      Continue to skip this step.{' '}
                      {isDemo
                        ? 'On a real enrollment site, members enter their access code here.'
                        : 'The live site requires a real access code.'}
                    </div>
                  )}
                </div>
              </div>
              {error && (
                <p role='alert' className={`text-sm text-red-600 ${POSTHOG_UNMASK_CLASS}`}>
                  {error}
                </p>
              )}
            </form>
            <p className={`text-xs text-neutral-500 ${POSTHOG_UNMASK_CLASS}`}>
              {dropdown ? "Don't see your location?" : "Don't have a code?"} Ask your{' '}
              {group.benefitsContact ?? 'branch HR rep'}, or call the Concierge Team at{' '}
              <a href={`tel:${phoneDigits}`} className='font-semibold text-[var(--enroll-ink)]'>
                {group.supportPhone}
              </a>
              .
            </p>
          </div>
        </div>

        {/* Feature cards */}
        <div className={`space-y-4 ${POSTHOG_UNMASK_CLASS}`}>
          {FEATURES.map(({Icon, title, body}) => (
            <div
              key={title}
              className='flex gap-4 rounded-2xl border border-neutral-200 bg-white p-5'
            >
              <span className='inline-flex h-10 w-10 flex-none items-center justify-center rounded-full bg-blue-100 text-blue-700'>
                <Icon className='h-5 w-5' />
              </span>
              <div>
                <h3 className='text-base font-semibold text-neutral-900'>{title}</h3>
                <p className='mt-1 text-sm leading-relaxed text-neutral-600'>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
